// src/components/MarketInsights/MarketInsightsPage.tsx

import React, { useState, useCallback } from "react";
import {
  Box,
  Alert, Accordion, AccordionSummary, AccordionDetails,
  Typography,
  Button,
  Badge,
  Chip,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { usePersistedState } from "../../hooks/usePersistedState";

// Hooks
import {
  useMarketSummary,
  useFilterOptions,
  useAvailabilityTimeline,
  useActiveSpecials,
  usePricingStats,
  usePricingHistory,
  usePropertiesList,
  useUnitsByDate,
  useUnitDetail,
  useFiltersState,
  marketInsightsKeys,
} from "./hooks/useMarketInsights";

// Components
import SummaryCards from "./components/SummaryCards";
import AvailabilityTimeline from "./components/AvailabilityTimeline";
import PricingAnalytics from "./components/PricingAnalytics";
import PricingHistoryExplorer from "./components/PricingHistoryExplorer";
import PropertyExplorer from "./components/PropertyExplorer";
import SpecialsFeed from "./components/SpecialsFeed";
import FilterPanel from "./components/FilterPanel";
import UnitDetailDrawer from "./components/UnitDetailDrawer";
import UnitsListDrawer from "./components/UnitsListDrawer";
import {
  openMarketInsightsProperty,
  type MarketInsightsNavigate,
} from "./navigation";
import PageRefreshButton from "../PageRefreshButton";
import { PropertiesSectionHeader, propertyUpdateAge } from "../PropertiesShell";
import { LoadingTopBar } from "../common/PageLoadingScaffold";

// Types
import {
  GroupByOption,
  PricingHistoryGroupBy,
  PricingHistoryMetric,
  PropertyListItem,
  SortByOption,
  SortOrder,
  UnitDetail,
} from "./types";

const ReadPanel: React.FC<{ error: unknown; onRetry: () => void; children: React.ReactNode }> = ({error, onRetry, children}) => error
  ? <Alert severity="warning" action={<Button onClick={onRetry}>Retry</Button>}>Couldn't load this panel.</Alert>
  : <>{children}</>;

interface MarketInsightsPageProps {
  onNavigate?: MarketInsightsNavigate;
}

const MarketInsightsPage: React.FC<MarketInsightsPageProps> = ({
  onNavigate,
}) => {
  const queryClient = useQueryClient();

  // Filter state
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
  } = useFiltersState();

  // UI State
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [openPanels,setOpenPanels]=useState({timeline:false,pricing:false,history:false});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [unitsListOpen, setUnitsListOpen] = useState(false);
  const [unitDetailOpen, setUnitDetailOpen] = useState(false);
  const [pricingGroupBy, setPricingGroupBy] = usePersistedState<GroupByOption>(
    "marketInsights.pricingGroupBy",
    "neighborhood"
  );
  const [pricingHistoryGroupBy, setPricingHistoryGroupBy] =
    usePersistedState<PricingHistoryGroupBy>(
      "marketInsights.pricingHistoryGroupBy",
      "bedrooms"
    );
  const [pricingHistoryMetric, setPricingHistoryMetric] =
    usePersistedState<PricingHistoryMetric>(
      "marketInsights.pricingHistoryMetric",
      "avg_price"
    );
  const [pricingHistoryDaysBack, setPricingHistoryDaysBack] =
    usePersistedState<number>(
      "marketInsights.pricingHistoryDaysBack",
      90
    );

  // Timeline state - NEW
  const [includeAvailableNow, setIncludeAvailableNow] =
    usePersistedState<boolean>(
      "marketInsights.includeAvailableNow",
      false
    );

  // Table state
  const [sortBy, setSortBy] = usePersistedState<SortByOption>(
    "marketInsights.sortBy",
    "available_now"
  );
  const [sortOrder, setSortOrder] = usePersistedState<SortOrder>(
    "marketInsights.sortOrder",
    "desc"
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedProperties, setSelectedProperties] = useState<PropertyListItem[]>([]);
  const selectedPropertyIds = selectedProperties.map((property) => property.id);
  // Always load pricing history. With no property selected the query runs
  // market-wide (grouped by bedrooms via effectivePricingHistoryGroupBy), so
  // the 77k-row trend engine shows a live default chart instead of an empty
  // "select a property" state. Selecting properties then narrows it.
  const shouldLoadPricingHistory = openPanels.history;
  // With nothing selected the query is MARKET-WIDE (all active properties):
  // coerce per-property groupings to bedrooms (floor_plan was always coerced;
  // a persisted "property" grouping otherwise renders an unlabeled top-N
  // properties chart), and clamp days_back to 90 — a market-wide 365-day
  // sweep is a paginated full-table crawl on the backend.
  const isMarketWidePricingHistory = selectedPropertyIds.length === 0;
  const effectivePricingHistoryGroupBy: PricingHistoryGroupBy =
    isMarketWidePricingHistory &&
    (pricingHistoryGroupBy === "floor_plan" ||
      pricingHistoryGroupBy === "property")
      ? "bedrooms"
      : pricingHistoryGroupBy;
  const effectivePricingHistoryDaysBack = isMarketWidePricingHistory
    ? Math.min(pricingHistoryDaysBack, 90)
    : pricingHistoryDaysBack;

  // Data queries
  const { data: summaryData, isLoading: summaryLoading, isFetching: summaryFetching, error: summaryError, refetch: summaryRetry } =
    useMarketSummary(filters);
  const { data: filterOptions, isLoading: filtersLoading, isFetching: filtersFetching } = useFilterOptions();
  const { data: timelineData, isLoading: timelineLoading, isFetching: timelineFetching, error: timelineError, refetch: timelineRetry } =
    useAvailabilityTimeline(filters, 90, includeAvailableNow, {enabled:openPanels.timeline}); // UPDATED - pass includeAvailableNow
  const { data: specialsData, isLoading: specialsLoading, isFetching: specialsFetching, error: specialsError, refetch: specialsRetry } =
    useActiveSpecials(filters);
  const { data: pricingData, isLoading: pricingLoading, isFetching: pricingFetching, error: pricingError, refetch: pricingRetry } = usePricingStats(
    pricingGroupBy,
    filters,
    {enabled:openPanels.pricing}
  );
  const {
    data: pricingHistoryData,
    isLoading: pricingHistoryLoading,
    error: pricingHistoryError, refetch: pricingHistoryRetry,
  } = usePricingHistory(
    effectivePricingHistoryGroupBy,
    effectivePricingHistoryDaysBack,
    selectedPropertyIds,
    filters,
    { enabled: shouldLoadPricingHistory }
  );
  const { data: propertiesData, isLoading: propertiesLoading, isFetching: propertiesFetching, error: propertiesError, refetch: propertiesRetry } =
    usePropertiesList(
      filters,
      { by: sortBy, order: sortOrder },
      {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }
    );
  const { data: unitsByDate, isLoading: unitsByDateLoading } = useUnitsByDate(
    selectedDate,
    filters
  );
  const { data: selectedUnit, isLoading: unitDetailLoading } =
    useUnitDetail(selectedUnitId);

  // Handlers
  const handleTimelineBarClick = useCallback((date: string) => {
    if (date) {
      setSelectedDate(date);
      setUnitsListOpen(true);
    } else {
      setSelectedDate(null);
      setUnitsListOpen(false);
    }
  }, []);

  const handleUnitClick = useCallback((unit: UnitDetail) => {
    setSelectedUnitId(unit.id);
    setUnitDetailOpen(true);
  }, []);

  const handlePropertyClick = useCallback(
    (propertyId: string) => {
      openMarketInsightsProperty(onNavigate, propertyId);
    },
    [onNavigate]
  );

  const handlePropertyToggle = useCallback(
    (property: PropertyListItem) => {
      const isAlreadySelected = selectedProperties.some(
        (item) => item.id === property.id
      );

      const nextSelectedProperties = isAlreadySelected
        ? selectedProperties.filter((item) => item.id !== property.id)
        : selectedProperties.length >= 4
          ? [...selectedProperties.slice(1), property]
          : [...selectedProperties, property];

      if (nextSelectedProperties.length === 0) {
        setPricingHistoryGroupBy("bedrooms");
      } else if (nextSelectedProperties.length === 1) {
        setPricingHistoryGroupBy("floor_plan");
      } else {
        setPricingHistoryGroupBy("property");
      }

      setSelectedProperties(nextSelectedProperties);
    },
    [selectedProperties, setPricingHistoryGroupBy]
  );

  const handleClearSelectedProperty = useCallback(
    (propertyId: string) => {
      const nextSelectedProperties = selectedProperties.filter(
        (property) => property.id !== propertyId
      );
      if (nextSelectedProperties.length === 0) {
        setPricingHistoryGroupBy("bedrooms");
      } else if (nextSelectedProperties.length === 1) {
        setPricingHistoryGroupBy("floor_plan");
      } else {
        setPricingHistoryGroupBy("property");
      }
      setSelectedProperties(nextSelectedProperties);
    },
    [selectedProperties, setPricingHistoryGroupBy]
  );

  const handleSortChange = useCallback(
    (newSortBy: SortByOption, newSortOrder: SortOrder) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      setPage(0);
    },
    []
  );

  const refreshing =
    summaryFetching ||
    filtersFetching ||
    timelineFetching ||
    specialsFetching ||
    pricingFetching ||
    propertiesFetching;

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: marketInsightsKeys.all });
  }, [queryClient]);

  return (
    <Box>
      {/* Header */}
      <PropertiesSectionHeader
        context={summaryData && !summaryError ? [
          [
            Number.isFinite(summaryData.total_units) ? `${summaryData.total_units} units` : null,
            Number.isFinite(summaryData.total_properties) ? `${summaryData.total_properties} buildings` : null,
          ].filter(Boolean).join(' across ') + (hasActiveFilters ? ' in the current filters' : ' market-wide'),
          propertyUpdateAge(summaryData.data_as_of) ? `data as of ${propertyUpdateAge(summaryData.data_as_of)}` : null,
        ].filter(Boolean).join(' · ') : null}
        actions={<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {hasActiveFilters && (
            <Chip
              label={`${activeFilterCount} filter${
                activeFilterCount > 1 ? "s" : ""
              } active`}
              onDelete={resetFilters}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          <Button
            variant="outlined"
            startIcon={
              <Badge badgeContent={activeFilterCount} color="primary">
                <FilterIcon />
              </Badge>
            }
            onClick={() => setFilterPanelOpen(true)}
          >
            Filters
          </Button>
          <PageRefreshButton iconOnly loading={Boolean(refreshing)} onClick={handleRefresh} />
        </Box>}
      />

      {/* Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <ReadPanel error={summaryError} onRetry={() => { void summaryRetry(); }}>
<SummaryCards data={summaryData} isLoading={summaryLoading} onNavigate={onNavigate} />
</ReadPanel>
      </Box>

      {/* Loading bar — placed above Upcoming Availability section
          (Grant 2026-04-25 — "above 'Upcoming Availability' on market insights tab") */}
      <LoadingTopBar
        loading={
          summaryLoading ||
          filtersLoading ||
          timelineLoading ||
          specialsLoading ||
          pricingLoading ||
          propertiesLoading
        }
        refreshing={Boolean(
          summaryFetching ||
            filtersFetching ||
            timelineFetching ||
            specialsFetching ||
            pricingFetching ||
            propertiesFetching
        )}
      />

      {/* Specials + Timeline Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={4}>
          <Box sx={{ height: specialsData?.length ? 420 : 180 }}>
            <ReadPanel error={specialsError} onRetry={() => { void specialsRetry(); }}>
<SpecialsFeed data={specialsData} isLoading={specialsLoading} />
</ReadPanel>
          </Box>
        </Grid>
        <Grid item xs={12} lg={8}>
          <Box>
            <Accordion expanded={openPanels.timeline} onChange={(_,expanded)=>setOpenPanels(prev=>({...prev,timeline:expanded}))}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Upcoming availability</Typography></AccordionSummary><AccordionDetails>
<ReadPanel error={timelineError} onRetry={() => { void timelineRetry(); }}>
<AvailabilityTimeline
              data={timelineData}
              isLoading={timelineLoading}
              onBarClick={handleTimelineBarClick}
              selectedDate={selectedDate}
              includeAvailableNow={includeAvailableNow}
              onIncludeAvailableNowChange={setIncludeAvailableNow}
            />
</ReadPanel>
<Typography variant="caption">{timelineData ? `${timelineData.reduce((n,d)=>n+d.count,0)} available units across ${timelineData.length} date buckets` : "No data loaded"} · capture freshness unknown.</Typography></AccordionDetails></Accordion>
          </Box>
        </Grid>
      </Grid>

      {/* Pricing Analytics */}
      <Box sx={{ mb: 3 }}>
        <Accordion expanded={openPanels.pricing} onChange={(_,expanded)=>setOpenPanels(prev=>({...prev,pricing:expanded}))}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Pricing</Typography></AccordionSummary><AccordionDetails>
<ReadPanel error={pricingError} onRetry={() => { void pricingRetry(); }}>
<PricingAnalytics
          data={pricingData}
          isLoading={pricingLoading}
          groupBy={pricingGroupBy}
          onGroupByChange={setPricingGroupBy}
        />
</ReadPanel>
<Typography variant="caption">{pricingData ? `${pricingData.reduce((n,d)=>n+d.count,0)} units across ${pricingData.length} price groups` : "No data loaded"} · capture freshness unknown.</Typography></AccordionDetails></Accordion>
      </Box>

      {/* Pricing History */}
      <Box sx={{ mb: 3 }}>
        <Accordion expanded={openPanels.history} onChange={(_,expanded)=>setOpenPanels(prev=>({...prev,history:expanded}))}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Rent trends</Typography></AccordionSummary><AccordionDetails>
<ReadPanel error={pricingHistoryError} onRetry={() => { void pricingHistoryRetry(); }}>
<PricingHistoryExplorer
          data={shouldLoadPricingHistory ? pricingHistoryData : undefined}
          isLoading={shouldLoadPricingHistory && pricingHistoryLoading}
          isDisabled={!shouldLoadPricingHistory}
          groupBy={effectivePricingHistoryGroupBy}
          onGroupByChange={setPricingHistoryGroupBy}
          daysBack={pricingHistoryDaysBack}
          onDaysBackChange={setPricingHistoryDaysBack}
          metric={pricingHistoryMetric}
          onMetricChange={setPricingHistoryMetric}
          selectedProperties={selectedProperties}
          onClearProperty={handleClearSelectedProperty}
        />
</ReadPanel>
<Typography variant="caption">{pricingHistoryData ? `${pricingHistoryData.series.length} series · ${pricingHistoryData.date_range.start} to ${pricingHistoryData.date_range.end}` : "No data loaded"} · historical observations; current freshness unknown.</Typography></AccordionDetails></Accordion>
      </Box>

      {/* Property Explorer */}
      <ReadPanel error={propertiesError} onRetry={() => { void propertiesRetry(); }}>
<PropertyExplorer
        data={propertiesData}
        isLoading={propertiesLoading}
        search={filters.search}
        onSearchChange={(value) => updateFilter("search", value)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(newRowsPerPage) => {
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
        selectedPropertyIds={selectedPropertyIds}
        onPropertyToggle={handlePropertyToggle}
        onPropertyOpen={handlePropertyClick}
      />
</ReadPanel>

      {/* Filter Panel */}
      <FilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={updateFilter}
        onReset={resetFilters}
        activeFilterCount={activeFilterCount}
      />

      {/* Units List Drawer (from timeline click) */}
      <UnitsListDrawer
        open={unitsListOpen}
        onClose={() => {
          setUnitsListOpen(false);
          setSelectedDate(null);
        }}
        units={unitsByDate}
        isLoading={unitsByDateLoading}
        selectedDate={selectedDate}
        onUnitClick={handleUnitClick}
      />

      {/* Unit Detail Drawer */}
      <UnitDetailDrawer
        open={unitDetailOpen}
        onClose={() => {
          setUnitDetailOpen(false);
          setSelectedUnitId(null);
        }}
        unit={selectedUnit || null}
        isLoading={unitDetailLoading}
        onPropertyClick={handlePropertyClick}
      />
    </Box>
  );
};

export default MarketInsightsPage;
